import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FlexBox from 'components/common/FlexBox';
import CityEditComponent from './CityEditComponent';
import CitiesTableComponent from './CitiesTableComponent';
import { CHILD_CITIES } from 'features/city/queries';
import useQuery from 'hooks/apollo/useQuery';
import { Box, Collapse, TableCell, TableRow } from '@mui/material';
import { CitiesContainer, CityEditCell, CityHeaderRow, CitySelectionCheckbox } from '../Styles';
import ActionsTableCell from 'components/common/table/ActionsTableCell';
import IconButton from 'components/common/buttons/IconButton';
import { Card, CollapseCard, Typography } from '@totalsoft/rocket-ui';
import { makeVar, useReactiveVar } from '@apollo/client';
import { isDistrictDirtyVar, selectedCityIdsVar } from 'features/city/variables';
import { getActionsSlotId } from 'features/city/functions';
import { isNewEntity } from 'utils/functions';
import { CityType } from 'features/city/constants';
import { useTranslation } from 'react-i18next';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_CITIES_WRITE } from 'constants/rightsCodes';
import CityIcon from '../CityIcon';

const cancelCityAddRef = makeVar();

const DistrictComponent = ({ data, onDistrictSaved }) => {
  const { t } = useTranslation();

  const isNew = useMemo(() => isNewEntity(data), [data]);

  // #region Load data

  const {
    data: citiesData,
    loading,
    refetch
  } = useQuery(CHILD_CITIES, {
    variables: { parentId: data.id },
    skip: !data.id || isNew
  });

  const cities = useMemo(() => citiesData?.childCities || [], [citiesData]);

  // #endregion

  const handleCitySaved = useCallback(
    city => {
      isDistrictDirtyVar(false);
      const wasNew = !cities.some(d => d.id === city.id);
      if (wasNew) {
        refetch();
        const cancelAdd = cancelCityAddRef();
        cancelAdd && cancelAdd();
      }
    },
    [refetch, cities]
  );

  const handleCitiesDeleted = useCallback(
    cityIds => {
      if (cities.some(city => !cityIds.includes(city.id))) return;
      const ids = selectedCityIdsVar();
      selectedCityIdsVar(ids.filter(id => !cityIds.includes(id)));
      refetch();
    },
    [refetch, cities]
  );

  const handleDirty = useCallback(() => {
    isDistrictDirtyVar(true);
  }, []);

  const InternalCityRowComponent = useCallback(
    ({ data }) => <CityRowComponent data={data} onCitySaved={handleCitySaved} onDirty={handleDirty} />,
    [handleCitySaved, handleDirty]
  );

  return (
    <FlexBox column gap={2}>
      <Box flexShrink={0}>
        <CollapseCard
          title={
            <FlexBox gap={0.5} alignItems='center'>
              <CityIcon type={CityType.District} />
              <Typography variant='subtitle1' fontWeight='bold'>{`${t('City.GeneralData')}${
                data.name ? ` - ${data.name}` : ''
              }`}</Typography>
            </FlexBox>
          }
          actions={<FlexBox id='district-header-actions-slot' />}
          defaultExpanded
        >
          <CityEditComponent data={data} actionsSlotId='district-header-actions-slot' onCitySaved={onDistrictSaved} onDirty={handleDirty} />
        </CollapseCard>
      </Box>
      <CitiesContainer column grow gap={2}>
        {isNew && (
          <FlexBox alignItems='center' justifyContent='center' height='100%'>
            <Typography variant='subtitle2' color='textPrimary' fontStyle='italic'>
              {t('City.SaveToAddCities')}
            </Typography>
          </FlexBox>
        )}
        {!isNew && (
          <CitiesTableComponent
            inlineFilters
            cities={cities}
            selectedIdsVar={selectedCityIdsVar}
            RowComponent={InternalCityRowComponent}
            type={CityType.Town}
            parentCityId={data.id}
            loading={loading}
            loadingLines={13}
            onCitiesDeleted={handleCitiesDeleted}
            cancelAddRef={cancelCityAddRef}
          />
        )}
      </CitiesContainer>
    </FlexBox>
  );
};

DistrictComponent.propTypes = {
  data: PropTypes.object,
  onDistrictSaved: PropTypes.func
};

export default DistrictComponent;

const CityRowComponent = ({ data, onCitySaved, onDirty }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const selectedCityIds = useReactiveVar(selectedCityIdsVar);

  const canWrite = usePermission(ADMIN_CITIES_WRITE);

  const selected = useMemo(() => selectedCityIds.includes(data.id), [selectedCityIds, data.id]);

  const handleSelect = useCallback(() => {
    const ids = selectedCityIdsVar();
    if (selected) selectedCityIdsVar(ids.filter(id => id !== data.id));
    else selectedCityIdsVar([...ids, data.id]);
  }, [selected, data.id]);

  const actionsSlotId = useMemo(getActionsSlotId, []);

  const isNew = useMemo(() => isNewEntity(data), [data]);

  return (
    <>
      <CityHeaderRow compact inactive={data.inactive}>
        <TableCell width={75}>
          <CitySelectionCheckbox checked={selected} onChange={handleSelect} disabled={isNew || !canWrite} />
        </TableCell>
        <TableCell>{isNew ? t('City.NewCity') : data.name}</TableCell>
        <ActionsTableCell>
          {expanded && <Box id={actionsSlotId} />}
          <IconButton size='tiny' type={expanded ? 'expandLess' : 'expandMore'} onClick={() => setExpanded(prev => !prev)} />
        </ActionsTableCell>
      </CityHeaderRow>
      <TableRow>
        <CityEditCell colSpan={3}>
          <Collapse in={expanded} mountOnEnter>
            <Card disablePadding sx={{ mx: 1, mb: 1.5 }}>
              <FlexBox p={2}>
                <CityEditComponent
                  data={data}
                  actionsSlotId={actionsSlotId}
                  saveIconSize='tiny'
                  onCitySaved={onCitySaved}
                  onDirty={onDirty}
                />
              </FlexBox>
            </Card>
          </Collapse>
        </CityEditCell>
      </TableRow>
    </>
  );
};

CityRowComponent.propTypes = {
  data: PropTypes.object,
  lens: PropTypes.object,
  onCitySaved: PropTypes.func,
  onDirty: PropTypes.func
};
